'use client';

import { type ReactNode, useEffect, useState } from 'react';

interface DeferredThirdPartyMountProps {
  children: ReactNode;
}

/**
 * 推迟挂载子组件：在 window load 之后、浏览器空闲时再渲染。
 * 避免在首屏 / hydration 阶段就插入第三方 <script>；比单独使用 next/script lazyOnload 更晚发起下载。
 */
export function DeferredThirdPartyMount({ children }: DeferredThirdPartyMountProps) {
  // This component is mounted directly under the locale layout. Reading the pathname with
  // next/navigation here makes Next treat dynamic modal routes as uncached during prerender.
  // Resolve it after hydration instead; keeping the initial value unknown also prevents the
  // third-party children from being rendered into Stream pages during the server pass.
  const [isStreamPage, setIsStreamPage] = useState<boolean | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const pathname = window.location.pathname;
    setIsStreamPage(
      pathname === '/vidu-stream' ||
        pathname.startsWith('/vidu-stream/') ||
        /^\/[^/]+\/vidu-stream(?:\/|$)/.test(pathname),
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    let idleId: number | undefined;
    /** 使用 number，避免与 @types/node 下 setTimeout 的 NodeJS.Timeout 冲突 */
    let fallbackTimerId: number | undefined;

    const mountWhenIdle = () => {
      const enable = () => {
        if (!cancelled) {
          setIsReady(true);
        }
      };
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(enable, { timeout: 8000 });
      } else {
        fallbackTimerId = window.setTimeout(enable, 2000) as unknown as number;
      }
    };

    const onWindowLoad = () => {
      mountWhenIdle();
    };

    if (document.readyState === 'complete') {
      mountWhenIdle();
    } else {
      window.addEventListener('load', onWindowLoad);
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', onWindowLoad);
      if (idleId !== undefined && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (fallbackTimerId !== undefined) {
        window.clearTimeout(fallbackTimerId);
      }
    };
  }, []);

  if (isStreamPage !== false || !isReady) {
    return null;
  }

  return children;
}
